/**
 * Modal de validation/rejet d'une worksheet (Manager)
 */

import { useState } from 'react';
import { Modal } from '../ui/Modal';
import { Button } from '../ui/Button';
import { Textarea } from '../ui/Textarea';
import { Worksheet } from '../../types/worksheets.types';
import { validateWorksheet } from '../../services/worksheets.api';

interface WorksheetValidationModalProps {
  worksheet: Worksheet;
  onClose: () => void;
}

export default function WorksheetValidationModal({
  worksheet,
  onClose,
}: WorksheetValidationModalProps) {
  const [action, setAction] = useState<'approve' | 'reject'>('approve');
  const [rejectionReason, setRejectionReason] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async () => {
    if (action === 'reject' && !rejectionReason.trim()) {
      setError('Veuillez indiquer une raison de rejet');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      await validateWorksheet(worksheet.id, {
        action,
        rejection_reason: action === 'reject' ? rejectionReason : undefined,
      });

      onClose();
    } catch (err: any) {
      console.error('Validation failed:', err);
      setError(err.response?.data?.message || 'Erreur lors de la validation');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      isOpen={true}
      onClose={onClose}
      title="Validation de la Worksheet"
      size="md"
      footer={
        <>
          <Button variant="outline" onClick={onClose} disabled={loading}>
            Annuler
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={loading}
            variant={action === 'reject' ? 'danger' : 'primary'}
          >
            {loading ? 'En cours...' : action === 'approve' ? 'Valider' : 'Rejeter'}
          </Button>
        </>
      }
    >
      <div className="space-y-6">
        {/* Worksheet info */}
        <div className="bg-gray-50 p-4 rounded-lg">
          <h3 className="font-medium text-gray-900">{worksheet.title}</h3>
          {worksheet.description && (
            <p className="text-sm text-gray-500 mt-1">{worksheet.description}</p>
          )}
          <div className="mt-2 text-sm text-gray-500">
            Nombre de tâches: {worksheet.tasks?.length || 0}
          </div>
        </div>

        {/* Action selection */}
        <div className="space-y-3">
          <label className="block text-sm font-medium text-gray-700">
            Action
          </label>
          <div className="flex gap-4">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="radio"
                name="action"
                value="approve"
                checked={action === 'approve'}
                onChange={() => setAction('approve')}
                className="w-4 h-4 text-green-600"
              />
              <span className="text-green-700 font-medium">Approuver</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="radio"
                name="action"
                value="reject"
                checked={action === 'reject'}
                onChange={() => setAction('reject')}
                className="w-4 h-4 text-red-600"
              />
              <span className="text-red-700 font-medium">Rejeter</span>
            </label>
          </div>
        </div>

        {/* Rejection reason */}
        {action === 'reject' && (
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">
              Raison du rejet *
            </label>
            <Textarea
              value={rejectionReason}
              onChange={(e) => setRejectionReason(e.target.value)}
              placeholder="Expliquez pourquoi cette worksheet est rejetée..."
              rows={3}
              className="w-full"
            />
          </div>
        )}

        {/* Approval message */}
        {action === 'approve' && (
          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
            <div className="flex gap-3">
              <svg
                className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              <div>
                <p className="text-sm text-green-800">
                  La worksheet passera au statut <strong>A FAIRE</strong> et pourra
                  être assignée à un employé.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Error */}
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
            {error}
          </div>
        )}
      </div>
    </Modal>
  );
}
